import React, { useState, useEffect } from "react";
import {
  FiSave,
  FiEye,
  FiEyeOff,
  FiShield,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiFilter,
  FiX,
} from "react-icons/fi";
import { FaUniversity as FaBankIcon } from "react-icons/fa";
import Swal from "../../utils/swalCompat";
import PageHeader from "../../components/PageHeader";

import {
  BankAccountList,
  addBankAccount,
  updateBankAccount,
  updateBankAccountStatus,
  deleteBankAccount,
} from "../../api/endpoints";// <-- adjust path

// ============================================================
// ✅ ADD / EDIT BANK MODAL
// ============================================================
const BankModal = ({ show, setShow, editData, onSuccess }) => {
  const [formData, setFormData] = useState({
    accountHolderName: "",
    accountNumber: "",
    bankName: "",
    ifscCode: "",
    status: "INACTIVE",
  });
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) {
      if (editData) {
        setFormData({
          accountHolderName: editData.accountHolderName || "",
          accountNumber: editData.accountNumber || "",
          bankName: editData.bankName || "",
          ifscCode: editData.ifscCode || "",
          status: editData.status || "INACTIVE",
        });
      } else {
        setFormData({
          accountHolderName: "",
          accountNumber: "",
          bankName: "",
          ifscCode: "",
          status: "INACTIVE",
        });
      }
      setShowAccountNumber(false);
    }
  }, [show, editData]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "ifscCode") {
      setFormData({ ...formData, [name]: value.toUpperCase() });
    } else if (name === "accountNumber") {
      setFormData({ ...formData, [name]: value.replace(/\D/g, "") });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validations
    if (!formData.accountHolderName.trim()) {
      Swal.fire("Error", "Please enter account holder name", "error");
      return;
    }
    if (!formData.accountNumber.trim()) {
      Swal.fire("Error", "Please enter account number", "error");
      return;
    }
    if (
      formData.accountNumber.length < 9 ||
      formData.accountNumber.length > 18
    ) {
      Swal.fire("Error", "Account number must be 9-18 digits", "error");
      return;
    }
    if (!formData.bankName.trim()) {
      Swal.fire("Error", "Please enter bank name", "error");
      return;
    }
    if (!formData.ifscCode.trim()) {
      Swal.fire("Error", "Please enter IFSC code", "error");
      return;
    }

    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    if (!ifscRegex.test(formData.ifscCode)) {
      Swal.fire(
        "Invalid IFSC Code",
        "Format: ABCD0123456 (4 letters, 0, 6 alphanumeric)",
        "error"
      );
      return;
    }

    try {
      setSaving(true);

      // ✅ API CALL
      let response;
      if (editData) {
        response = await updateBankAccount(editData._id, {
          accountHolderName: formData.accountHolderName,
          accountNumber: formData.accountNumber,
          bankName: formData.bankName,
          ifscCode: formData.ifscCode,
          status: formData.status,
        });
      } else {
        response = await addBankAccount({
          accountHolderName: formData.accountHolderName,
          accountNumber: formData.accountNumber,
          bankName: formData.bankName,
          ifscCode: formData.ifscCode,
          status: formData.status,
        });
      }

      // ✅ Handle both success:true and success:false
      if (response?.success === false) {
        Swal.fire("Error", response?.message || "Something went wrong", "error");
        return;
      }

      Swal.fire({
        icon: "success",
        title: editData ? "Updated!" : "Added!",
        text: `Bank account ${editData ? "updated" : "added"} successfully`,
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: "top-end",
      });

      setShow(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Bank save error:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.message || "Failed to save bank account",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show d-block"
      style={{ zIndex: 1050 }}
      tabIndex="-1"
      onClick={() => setShow(false)}
    >
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content border-0 premium-form-modal">
          {/* Header */}
          <div
            className="modal-header border-0"

          >
            <div className="d-flex align-items-center gap-2">
              <div
                className="d-flex align-items-center justify-content-center rounded-3"
                style={{
                  width: 36,
                  height: 36,
                  background: "rgba(255,255,255,0.2)",
                }}
              >
                <FaBankIcon size={15} />
              </div>
              <div>
                <h5 className="modal-title fw-bold mb-0">
                  {editData ? "Edit Bank Account" : "Add Bank Account"}
                </h5>
                <small>
                  {editData
                    ? "Update bank account details"
                    : "Add a new bank account for withdrawals"}
                </small>
              </div>
            </div>
            <button
              type="button"
              className="btn-close"
              onClick={() => setShow(false)}
            ></button>
          </div>

          {/* Body */}
          <div className="modal-body p-4">
            <form onSubmit={handleSubmit} id="bankForm">
              <div className="row g-3">
                {/* Account Holder Name */}
                <div className="col-md-6 col-12">
                  <label
                    className="form-label fw-semibold"
                    style={{ fontSize: "0.8rem" }}
                  >
                    Account Holder Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="accountHolderName"
                    className="form-control"
                    value={formData.accountHolderName}
                    onChange={handleChange}
                    disabled={saving}
                    placeholder="e.g. Ram Rummy Official"
                    autoFocus
                  />
                </div>

                {/* Bank Name */}
                <div className="col-md-6 col-12">
                  <label
                    className="form-label fw-semibold"
                    style={{ fontSize: "0.8rem" }}
                  >
                    Bank Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="bankName"
                    className="form-control"
                    value={formData.bankName}
                    onChange={handleChange}
                    disabled={saving}
                    placeholder="e.g. State Bank of India"
                  />
                </div>

                {/* Account Number */}
                <div className="col-md-6 col-12">
                  <label
                    className="form-label fw-semibold"
                    style={{ fontSize: "0.8rem" }}
                  >
                    Account Number <span className="text-danger">*</span>
                  </label>
                  <div className="position-relative">
                    <input
                      type={showAccountNumber ? "text" : "password"}
                      name="accountNumber"
                      className="form-control"
                      value={formData.accountNumber}
                      onChange={handleChange}
                      disabled={saving}
                      placeholder="Enter account number"
                      maxLength={18}
                      style={{
                        paddingRight: 52,
                        fontFamily: showAccountNumber
                          ? "inherit"
                          : "monospace",
                        letterSpacing: showAccountNumber ? "normal" : "2px",
                      }}
                    />
                    <button
                      type="button"
                      className="account-num-toggle-btn"
                      onClick={() =>
                        setShowAccountNumber(!showAccountNumber)
                      }
                      title={
                        showAccountNumber
                          ? "Hide account number"
                          : "Show account number"
                      }
                      tabIndex={-1}
                    >
                      {showAccountNumber ? (
                        <FiEye size={18} />
                      ) : (
                        <FiEyeOff size={18} />
                      )}
                    </button>
                  </div>
                  <small className="text-muted" style={{ fontSize: "0.7rem" }}>
                    9-18 digits
                  </small>
                </div>

                {/* IFSC Code */}
                <div className="col-md-6 col-12">
                  <label
                    className="form-label fw-semibold"
                    style={{ fontSize: "0.8rem" }}
                  >
                    IFSC Code <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="ifscCode"
                    className="form-control"
                    value={formData.ifscCode}
                    onChange={handleChange}
                    disabled={saving}
                    placeholder="e.g. SBIN0001234"
                    maxLength={11}
                    style={{
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                    }}
                  />
                  <small className="text-muted" style={{ fontSize: "0.7rem" }}>
                    Format: ABCD0123456 (11 characters)
                  </small>
                </div>

                {/* Status */}
                <div className="col-12">
                  <label
                    className="form-label fw-semibold"
                    style={{ fontSize: "0.8rem" }}
                  >
                    Status
                  </label>
                  <select
                    name="status"
                    className="form-select"
                    value={formData.status}
                    onChange={handleChange}
                    disabled={saving}
                  >
                    <option value="INACTIVE">INACTIVE</option>
                    <option value="ACTIVE">ACTIVE</option>
                  </select>
                  <small
                    className="fw-semibold"
                    style={{
                      fontSize: "0.7rem",
                      color:
                        formData.status === "ACTIVE" ? "#16A34A" : "#DC2626",
                    }}
                  >
                    {formData.status === "ACTIVE"
                      ? "✓ Active — visible to users"
                      : "✗ Inactive — hidden from users"}
                  </small>
                </div>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="modal-footer border-0">
            <button
              type="button"
              className="btn btn-light border"
              onClick={() => setShow(false)}
              disabled={saving}

            >
              Cancel
            </button>
            <button
              type="submit"
              form="bankForm"
              className="btn btn-primary d-flex align-items-center gap-2"
              disabled={saving}

            >
              {saving ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                  ></span>
                  Saving...
                </>
              ) : (
                <>
                  {editData ? <FiSave size={13} /> : <FiPlus size={13} />}
                  {editData ? "Update" : "Add Bank"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const AccountSetting = () => {
  const [bankList, setBankList] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showFilter, setShowFilter] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);

  // Per-row account visibility
  const [visibleAccounts, setVisibleAccounts] = useState({});

  // ============ FETCH BANK LIST ============
  const fetchBankList = async () => {
    try {
      setLoading(true);
      const response = await BankAccountList();

      if (response?.success === false) {
        Swal.fire("Error", response?.message || "Failed to load banks", "error");
        setBankList([]);
        return;
      }

      // ✅ Actual response shape: response.data.banks
      const list = response?.data?.banks || [];

      setBankList(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Fetch bank list error:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.message || "Failed to load bank accounts",
        "error"
      );
      setBankList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBankList();
  }, []);

  // ============ FILTER LOGIC ============
  const filteredList = bankList.filter((item) => {
    const q = search.trim().toLowerCase();
    const matchesSearch =
      !q ||
      (item.accountHolderName || "").toLowerCase().includes(q) ||
      (item.bankName || "").toLowerCase().includes(q) ||
      (item.ifscCode || "").toLowerCase().includes(q) ||
      (item.accountNumber || "").includes(q);

    const matchesStatus = !statusFilter || item.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const hasActiveFilters = search || statusFilter;

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
  };

  // ============ MODAL HANDLERS ============
  const handleOpenAdd = () => {
    setEditData(null);
    setShowModal(true);
  };

  const handleOpenEdit = (item) => {
    setEditData(item);
    setShowModal(true);
  };

  const handleModalSuccess = () => {
    fetchBankList();
  };

  // ============ DELETE ============
  const handleDelete = async (id, bankName) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Delete bank account from "${bankName}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete",
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteBankAccount(id);

        if (response?.success === false) {
          Swal.fire("Error", response?.message || "Delete failed", "error");
          return;
        }

        setBankList((prev) => prev.filter((item) => item._id !== id));

        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "Bank account deleted successfully",
          timer: 1500,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      } catch (error) {
        console.error("Delete error:", error);
        Swal.fire(
          "Error",
          error?.response?.data?.message || "Failed to delete bank account",
          "error"
        );
      }
    }
  };

  // ============ STATUS TOGGLE ============
  const handleStatusToggle = async (id, currentStatus) => {
    const newStatus = currentStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    const action = newStatus === "ACTIVE" ? "activate" : "deactivate";

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You want to ${action} this bank account?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: newStatus === "ACTIVE" ? "#28a745" : "#d33",
      confirmButtonText: `Yes, ${action}`,
    });

    if (result.isConfirmed) {
      try {
        const response = await updateBankAccountStatus(id, newStatus);

        if (response?.success === false) {
          Swal.fire(
            "Error",
            response?.message || "Status update failed",
            "error"
          );
          return;
        }

        setBankList((prev) =>
          prev.map((item) =>
            item._id === id ? { ...item, status: newStatus } : item
          )
        );

        Swal.fire({
          icon: "success",
          title: "Success",
          text: `Bank account ${action}d successfully`,
          timer: 1500,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      } catch (error) {
        console.error("Status update error:", error);
        Swal.fire(
          "Error",
          error?.response?.data?.message || "Failed to update status",
          "error"
        );
      }
    }
  };

  // ============ TOGGLE ACCOUNT VISIBILITY ============
  const toggleAccountVisibility = (id) => {
    setVisibleAccounts((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // ============ FORMATTERS ============
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const maskAccountNumber = (acc) => {
    if (!acc) return "N/A";
    return "•••• " + acc.slice(-4);
  };

  return (
    <div className="lead-page account-setting-page">
      <PageHeader
        eyebrow="Admin manager"
        title="Account Setting"
        subtitle="Manage bank accounts for withdrawals and settlement flows with a unified premium layout."
        icon={<FiShield />}
        tone="warning"
        actions={(
          <button
            className="btn btn-danger d-inline-flex align-items-center gap-2"
            onClick={handleOpenAdd}
          >
            <FiPlus size={14} /> Add Bank
          </button>
        )}
      />

      {/* MAIN CARD */}
      <div className="card mb-4 card-light">
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
          <h5 className="card-title mb-0">Bank Accounts</h5>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            {showFilter && (
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search account, bank, IFSC..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ width: "240px" }}
                />

                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{ width: "140px" }}
                >
                  <option value="">All Status</option>
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>

                {hasActiveFilters && (
                  <button
                    className="btn btn-outline-danger"
                    onClick={clearFilters}
                    title="Clear filters"
                  >
                    <FiX /> Clear
                  </button>
                )}
              </div>
            )}
            <button
              className={`btn ${showFilter ? "btn-primary" : "btn-dark"}`}
              onClick={() => setShowFilter(!showFilter)}
            >
              <FiFilter />
              {showFilter ? " Hide" : " Filter"}
            </button>
          </div>
        </div>

        <div className="card-body">
          <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <span className="text-muted">
                Showing {filteredList.length} of {bankList.length} accounts
              </span>
              {hasActiveFilters && (
                <span className="badge bg-info ms-2">Filters Applied</span>
              )}
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mb-3 d-flex gap-2 flex-wrap">
              {search && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  Search: {search}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => setSearch("")}
                    style={{ background: "none", border: "none" }}
                  >
                    <FiX size={11} />
                  </button>
                </span>
              )}
              {statusFilter && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  Status: {statusFilter}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => setStatusFilter("")}
                    style={{ background: "none", border: "none" }}
                  >
                    <FiX size={11} />
                  </button>
                </span>
              )}
            </div>
          )}

          {/* Table */}
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Account Holder</th>
                  <th>Account Number</th>
                  <th>Bank Name</th>
                  <th>IFSC Code</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Loading bank accounts...
                    </td>
                  </tr>
                ) : filteredList.length > 0 ? (
                  filteredList.map((item, index) => (
                    <tr key={item._id}>
                      <td>{index + 1}</td>

                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded-circle text-white d-flex align-items-center justify-content-center"
                            style={{
                              width: 35,
                              height: 35,
                              background:
                                "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
                            }}
                          >
                            <FaBankIcon size={13} />
                          </div>
                          <span className="fw-semibold">
                            {item.accountHolderName}
                          </span>
                        </div>
                      </td>

                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="fw-semibold"
                            style={{
                              fontFamily: visibleAccounts[item._id]
                                ? "inherit"
                                : "monospace",
                              letterSpacing: visibleAccounts[item._id]
                                ? "normal"
                                : "1.5px",
                              fontSize: "0.85rem",
                            }}
                          >
                            {visibleAccounts[item._id]
                              ? item.accountNumber
                              : maskAccountNumber(item.accountNumber)}
                          </span>
                          <button
                            type="button"
                            className="account-num-toggle-btn-list"
                            onClick={() => toggleAccountVisibility(item._id)}
                            title={
                              visibleAccounts[item._id]
                                ? "Hide account number"
                                : "Show account number"
                            }
                          >
                            {visibleAccounts[item._id] ? (
                              <FiEye size={13} />
                            ) : (
                              <FiEyeOff size={13} />
                            )}
                          </button>
                        </div>
                      </td>

                      <td>{item.bankName}</td>

                      <td>
                        <span
                          className="badge"
                          style={{
                            background: "#EFF6FF",
                            color: "#1D4ED8",
                            fontWeight: 600,
                            letterSpacing: "0.5px",
                          }}
                        >
                          {item.ifscCode}
                        </span>
                      </td>

                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <label
                            style={{
                              position: "relative",
                              display: "inline-block",
                              width: "50px",
                              height: "24px",
                              marginBottom: 0,
                              cursor: "pointer",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={item.status === "ACTIVE"}
                              onChange={() =>
                                handleStatusToggle(item._id, item.status)
                              }
                              style={{ opacity: 0, width: 0, height: 0 }}
                            />
                            <span
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background:
                                  item.status === "ACTIVE"
                                    ? "#4CAF50"
                                    : "#ccc",
                                transition: ".4s",
                                borderRadius: "34px",
                              }}
                            >
                              <span
                                style={{
                                  position: "absolute",
                                  height: "16px",
                                  width: "16px",
                                  left:
                                    item.status === "ACTIVE"
                                      ? "26px"
                                      : "4px",
                                  bottom: "4px",
                                  background: "white",
                                  transition: ".4s",
                                  borderRadius: "50%",
                                }}
                              />
                            </span>
                          </label>
                          <span
                            className={`badge ${
                              item.status === "ACTIVE"
                                ? "bg-success"
                                : "bg-danger"
                            }`}
                            style={{ minWidth: "70px" }}
                          >
                            {item.status}
                          </span>
                        </div>
                      </td>

                      <td>{formatDate(item.createdAt)}</td>

                      <td>
                        <div className="d-flex gap-2 justify-content-center">
                          <button
                            className="btn btn-sm btn-info custum_edit text-white"
                            onClick={() => handleOpenEdit(item)}
                            title="Edit"
                          >
                            <FiEdit size={13} />
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() =>
                              handleDelete(item._id, item.bankName)
                            }
                            title="Delete"
                          >
                            <FiTrash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      {hasActiveFilters
                        ? "No bank accounts found matching your filters"
                        : "No bank accounts found — Click 'Add Bank' to add one"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* MODAL */}
      <BankModal
        show={showModal}
        setShow={setShowModal}
        editData={editData}
        onSuccess={handleModalSuccess}
      />

      {/* CUSTOM STYLES */}
      <style>{`
        .account-num-toggle-btn {
          position: absolute;
          right: 6px;
          top: 50%;
          transform: translateY(-50%);
          width: 38px;
          height: 38px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #fff;
          border: 1.5px solid #1E3A5F;
          border-radius: 8px;
          color: #1E3A5F;
          cursor: pointer;
          transition: all 0.2s ease;
          padding: 0;
          z-index: 5;
        }
        .account-num-toggle-btn:hover {
          background: #F1F5F9;
          border-color: #0F172A;
          color: #0F172A;
        }
        .account-num-toggle-btn:active {
          transform: translateY(-50%) scale(0.95);
        }
        .account-num-toggle-btn:focus {
          outline: none;
          box-shadow: 0 0 0 3px rgba(30, 58, 95, 0.15);
        }

        .account-num-toggle-btn-list {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 26px;
          height: 26px;
          background: #fff;
          border: 1.2px solid #1E3A5F;
          border-radius: 6px;
          color: #1E3A5F;
          cursor: pointer;
          transition: all 0.2s ease;
          padding: 0;
          flex-shrink: 0;
        }
        .account-num-toggle-btn-list:hover {
          background: #F1F5F9;
          border-color: #0F172A;
          color: #0F172A;
        }
        .account-num-toggle-btn-list:active {
          transform: scale(0.92);
        }
        .account-num-toggle-btn-list:focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba(30, 58, 95, 0.15);
        }
      `}</style>
    </div>
  );
};

export default AccountSetting;